import { useMemo, type MouseEvent } from "react";
import CheckIcon from "@heroicons/react/24/solid/CheckIcon";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import {
    createColumnHelper,
    type CellContext,
    type ColumnMeta,
} from "@tanstack/react-table";
import { shallow } from "zustand/shallow";

import { useBreakpointContext } from "@hsl/core/hooks/useBreakpoint";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import handleDisplayDirectives from "@hsl/core/utils/handleDisplayDirectives";
import { localeFormat } from "@hsl/fund-explorer/format";
import type {
    AllShareClassMeta,
    Listing,
} from "@hsl/fund-explorer/metaSchemas";
import {
    FieldMeta,
    type DocObj,
    type FormattedValue,
    type MetaData,
    type TopLevelDataObj,
} from "@hsl/fund-explorer/types";
import twColors from "@hsl/lgim/src/js/lib/themeColors";

import type {
    ColumnDisplayDirectives,
    TabColumnConfig,
    TabConfig,
    TabDisplayDirectives,
} from "../../../config/types";
import useIsEnglishLang from "../../../hooks/useIsEnglishLang";
import useTranslation, { Translate } from "../../../hooks/useTranslation";
import displayURLForFund from "../../../lib/displayURLForFund";
import { keyDocType } from "../../../lib/kidOrKiid";
import useExplorerStore from "../../../store/store";
import { DocumentDisplay } from "../../FundCardDocuments/components";
import { mergeDocs } from "../../FundCardDocuments/helpers/mergeDocs";
import { CaretTriangle } from "../../icons";
import PDFIcon from "../../PdfIcon/PdfIcon";
import { HeaderValue } from "../../Table";
import Tooltip from "../../Tooltip";
import HighlightOnMatch from "../components/HighlightOnMatch";
import { type FundDeps } from "../Table";

// unicode non breaking space equal to the width of a fixed width number
export const FIGURE_SPACE = " ";
// unicode minus sign - a bit wider than a default hyphen
export const MINUS = "−";

export type ColDefMeta = ColumnMeta<TopLevelDataObj, FormattedValue>;

type ColKey = keyof (AllShareClassMeta & Listing);

export default function useColumns(
    tabConfig: TabConfig | undefined,
    fundDeps: FundDeps | undefined,
    data: TopLevelDataObj[] | undefined,
) {
    const tx = useTranslation();
    const noTxProps = ["minInvestment"];
    const currentBreakpoint = useBreakpointContext()?.currentBreakpoint;
    const useMobileCols =
        currentBreakpoint === "xs" &&
        tabConfig?.columnsMobile &&
        (tabConfig?.columnsMobile?.length ?? 0) > 0;

    const columnConfigs = useMobileCols
        ? tabConfig?.columnsMobile
        : tabConfig?.columns;
    const {
        getDataById,
        jurisdictionId,
        productType,
        locale,
        groupBy,
        rawData,
        getMeta,
    } = useExplorerStore(
        ({
            getDataById,
            config: { jurisdictionId, productType, locale },
            rawData,
            groupBy,
            getMeta,
        }) => ({
            getDataById,
            jurisdictionId,
            productType,
            locale,
            groupBy,
            rawData,
            getMeta,
        }),
        shallow,
    );
    const { clickLinkEvent } = useYardActionEvents();

    const isEnglish = useIsEnglishLang();

    const columnHelper = useMemo(
        () => createColumnHelper<TopLevelDataObj>(),
        [],
    );
    const getParent = (
        level: string,
        id: string | undefined,
    ): TopLevelDataObj | undefined => {
        if (!id) {
            return undefined;
        }
        const obj = getDataById(id);
        if (!obj || obj.type === level) {
            return obj;
        }
        return getParent(level, obj.parentId);
    };

    const log =
        (msg: string) =>
        <T,>(x: T) => {
            console.log(msg, x);
            return x;
        };
    const applyOverrides = (config: TabColumnConfig) => {
        const { overrides } = config;
        if (!overrides) {
            return config;
        }
        const oConfig = overrides
            .map((ov) => {
                if (ov.type === "fund") {
                    const _prop: string | undefined = ov.prop;
                    const datum = data![0];
                    const fund = datum
                        ? getParent("fund", datum.parentId)
                        : null;
                    if (!fund) {
                        return false;
                    }
                    const fundValue =
                        fund[_prop as keyof typeof _prop] ?? fund.data[_prop!];
                    return ov.value?.includes(fundValue) ? ov.override : false;
                }
                return false;
            })
            .reduce((acc, oRide) => (oRide ? { ...acc, ...oRide } : acc), {});
        return { ...config, ...oConfig } as TabColumnConfig;
    };
    const applyFundDependencyFilters = (config: TabColumnConfig) => {
        if (!fundDeps || !Object.entries(fundDeps).length) {
            return true;
        }
        return (
            config?.dependencies
                ?.filter((dep) => dep.level === "fund")
                .every((dep) => {
                    const val = fundDeps[dep.prop];
                    if (typeof val === "string") {
                        return dep.values.includes(val === "-" ? null : val);
                    }
                    return false;
                }) ?? true
        );
    };
    const configReducer = (
        clist: TabColumnConfig[],
        config: TabColumnConfig,
    ) => {
        if (!applyFundDependencyFilters(config)) {
            return clist;
        }
        const oConfig = applyOverrides(config);
        return oConfig.hidden !== undefined && oConfig.hidden
            ? clist
            : [...clist, oConfig];
    };
    const availableColumnConfigs = columnConfigs?.reduce(
        configReducer,
        [] as TabColumnConfig[],
    );
    const isKidOrKiid = (prop: string) => /^ki+d$/.test(prop);
    const docTypeProp = keyDocType(jurisdictionId, productType);
    const registerLinkClick = (
        datum: TopLevelDataObj,
        nameProp: string,
        codeProp: string,
    ) => {
        const _fund = datum.parentId
            ? getParent("fund", datum.parentId)
            : datum;
        const group = (_fund?.data[groupBy] ?? "All funds") as string;
        const shareClass = {
            name: datum.data[nameProp] as string,
            code: datum.data[codeProp] as string,
        };
        const fund = {
            name: (_fund?.data.name ?? "") as string,
            id: _fund?.id ?? 0,
            type: _fund?.type ?? "",
        };
        clickLinkEvent({
            fund,
            shareClass,
            group,
        });
    };

    const getColumnHeaderFromMeta = (prop: string, meta?: MetaData) => {
        return (
            meta &&
            [...meta.share_class_fields, ...meta.fund_fields].find(
                (f) => f.code_name === prop,
            )?.label
        );
    };

    if (!tabConfig || !columnConfigs || !availableColumnConfigs) {
        return [];
    }

    return availableColumnConfigs.map((config, i, arr) => {
        const { prop, label, level, localeSpecific } = config;

        function returnVal<T = string>(val: T) {
            return localeSpecific && val
                ? localeFormat(localeSpecific, locale, val as string)
                : val;
        }

        const accessor = (row: TopLevelDataObj) => {
            const _prop = isKidOrKiid(prop) ? docTypeProp : prop;
            const noTx = noTxProps.includes(_prop);
            if (level === row.type) {
                // Treat the Irish PMC explorer priceCurrency differently
                if (_prop === "priceCurrency" && productType === "pmc") {
                    const matchProp = `priceWeeklyMid${jurisdictionId === 78 ? "Eur" : ""}`;
                    const scMeta = getMeta("share_class_fields") as FieldMeta[];
                    const priceEurMeta = scMeta.find(
                        (m) => m.code_name === matchProp,
                    );
                    if (priceEurMeta) {
                        return priceEurMeta.currency;
                    }
                }
                return noTx ? row.data[_prop] : returnVal(row.data[_prop]);
            }
            const parent = getParent(level, row.parentId);

            if (parent) {
                if (!(_prop in parent.data))
                    throw new Error(`No ${_prop} in parent data`);
                return noTx
                    ? parent.data[_prop]
                    : returnVal(parent.data[_prop]);
            }
        };
        const colDef = {
            id: prop,
            header: () => (
                <HeaderValue
                    tooltip={{
                        content: tx(config.displayDirectives?.tooltip ?? "")
                            .result,
                        placement: "right",
                    }}
                >
                    <Translate>
                        {label === "metadata"
                            ? getColumnHeaderFromMeta(
                                  prop,
                                  rawData?.metadata,
                              ) ?? label
                            : label}
                    </Translate>
                </HeaderValue>
            ),
            cell: (info: CellContext<TopLevelDataObj, FormattedValue>) => (
                <>{info.getValue<string>()}</>
            ),
            meta: {} as ColDefMeta,
        };

        let headerParentClassName = "",
            cellParentClassName = "";

        switch (prop as ColKey) {
            // large fixed width
            case "exchangeName":
                colDef.header = () => (
                    <HeaderValue className="w-[150px]">
                        <Translate>{label}</Translate>
                    </HeaderValue>
                );
                headerParentClassName = "w-[15%] min-w-[15%]";
                break;
            // medium fixed width
            case "shortName":
                colDef.header = () => (
                    <HeaderValue className="w-[80px] sm:w-[120px]">
                        <Translate>{label}</Translate>
                    </HeaderValue>
                );
                headerParentClassName = "w-[10%] min-w-[10%]";
                colDef.cell = (info) => {
                    const shortName = info.getValue() as string;
                    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
                        e.stopPropagation();
                        registerLinkClick(info.row.original, prop, "isin");
                    };

                    const shareclassURL = data?.find((x) => {
                        const suffix = config.displayDirectives.suffix;
                        if (suffix) {
                            return (
                                x.data.shortName ===
                                shortName.replace(suffix, "").trimEnd()
                            );
                        }
                        return x.data.shortName === shortName;
                    })?.data.shareclassPageURL as string;

                    return (
                        <a
                            href={`${shareclassURL}&${new URLSearchParams(
                                window.location.search,
                            ).toString()}`}
                            onClick={handleClick}
                        >
                            {shortName}
                        </a>
                    );
                };
                cellParentClassName = "hover:underline cursor-pointer";
                break;
            case "iterationName":
            case "iterationDetail":
            case "iterationPriceSeries":
                cellParentClassName = headerParentClassName =
                    "w-[205px] whitespace-normal";
                colDef.cell = (info) => {
                    const code = info.getValue() as string;
                    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
                        e.stopPropagation();
                        registerLinkClick(info.row.original, prop, "scopeCode");
                    };
                    const url = info.row.original.data["iterationPageURL"] as
                        | string
                        | undefined;
                    const fund = getParent("fund", info.row.original.parentId);
                    const showURL = displayURLForFund(
                        "pmc",
                        fund?.data["card_variant"],
                    );

                    return url && url !== "-" && showURL ? (
                        <a
                            href={`${url}&${new URLSearchParams(
                                window.location.search,
                            ).toString()}`}
                            onClick={handleClick}
                        >
                            {code}
                        </a>
                    ) : (
                        <span className="text-charcoal font-normal">
                            {code}
                        </span>
                    );
                };
                break;
            case "iterationBenchmarkDetail":
                headerParentClassName = `${headerParentClassName} sm:w-[195px]`;
                break;
            case "isin":
                colDef.header = () => (
                    <HeaderValue className="w-[120px]">
                        <Translate>{label}</Translate>
                    </HeaderValue>
                );
                headerParentClassName = "w-[15%] min-w-[15%]";
                colDef.cell = (info) => (
                    <HighlightOnMatch text={info.getValue() as string} />
                );
                break;
            case "scopeCode":
                headerParentClassName = cellParentClassName = "w-[40px]";
                colDef.cell = (info) => (
                    <HighlightOnMatch text={info.getValue() as string} />
                );
                break;
            case "launchDate":
                if (productType === "pmc") {
                    headerParentClassName = cellParentClassName = "w-[80px]";
                }
                break;
            case "priceCurrency":
                headerParentClassName = cellParentClassName = "w-[90px]";
                break;
            // small fixed width
            case "indexTicker":
                colDef.header = () => (
                    <HeaderValue className="w-[80px]">
                        <Translate>{label}</Translate>
                    </HeaderValue>
                );
                headerParentClassName = "w-[15%] min-w-[15%]";
                break;
            case "ocf":
                headerParentClassName = "w-[10%]";
                break;
            case "factSheet":
            case "kiid":
            case "kid":
            case "memberCommunication":
                colDef.header = () => (
                    <HeaderValue alignCenter>
                        <Translate>
                            {isKidOrKiid(prop)
                                ? docTypeProp.toUpperCase()
                                : label}
                        </Translate>
                    </HeaderValue>
                );
                colDef.cell = (info) => {
                    const isPMC = productType === "pmc";
                    const memberComm =
                        prop === "factSheet"
                            ? info.row.original.data["memberCommunication"]
                            : "-";
                    const value = info.getValue<DocObj[] | string>();
                    const documents: DocObj[] =
                        isPMC && memberComm
                            ? mergeDocs(value, memberComm, (t, d) => ({
                                  ...d,
                                  lang: t === "doc1" ? "Gross" : "Net",
                              }))
                            : [];
                    const docWithNetGross =
                        !isPMC || typeof value === "string"
                            ? value
                            : value.map((v) =>
                                  prop === "factSheet"
                                      ? { ...v, lang: "Net" }
                                      : v,
                              );
                    const showLang =
                        !isEnglish || (isPMC && prop === "factSheet");
                    return (
                        <span className="mx-auto flex justify-center">
                            <DocumentDisplay
                                data={
                                    productType !== "pmc" ||
                                    documents.length === 0
                                        ? docWithNetGross
                                        : documents
                                }
                                icon={<PDFIcon />}
                                showLang={showLang}
                                type="pdf"
                                shareClass={info.row.original}
                            />
                        </span>
                    );
                };
                break;
            case "availableDB":
            case "availableDC":
            case "availableOLAB":
            case "availableUK":
                headerParentClassName = `${headerParentClassName} align-bottom ${
                    prop === "availableOLAB" && "pl-0"
                }`;
                colDef.cell = (info) => {
                    const value = info.getValue();
                    return (
                        <span className="mx-auto flex justify-center">
                            {value && value !== "-" ? (
                                <CheckIcon
                                    width={14}
                                    height={14}
                                    color={twColors["core-green"]}
                                />
                            ) : (
                                <XMarkIcon width={14} height={14} />
                            )}
                        </span>
                    );
                };
                break;
            case "midChange":
                cellParentClassName = `${cellParentClassName} w-[10%] pl-[2.5em]`;
                colDef.cell = (info) => {
                    const rawValue = info.getValue<string>();
                    const value = parseFloat(
                        rawValue.replace(",", ".").slice(0, -1),
                    );
                    const indicator = isNaN(value) ? (
                        ""
                    ) : value !== 0 ? (
                        <CaretTriangle
                            fill={
                                twColors[value > 0 ? "core-green" : "core-red"]
                            }
                            width={12}
                            height={12}
                            className={cx("ml-1", {
                                "rotate-180 transform": value < 0,
                            })}
                        />
                    ) : (
                        <div className="my-0.5 ml-1.5 mr-0.5 h-2 w-2 bg-[#FF9A4C]"></div>
                    );
                    return (
                        <span
                            className={cx("flex items-center", {
                                "justify-end":
                                    tabConfig.displayDirectives?.alignRight ||
                                    config.displayDirectives.alignRight,
                                "justify-center":
                                    tabConfig.displayDirectives?.alignCenter ||
                                    config.displayDirectives.alignCenter,
                                "justify-start":
                                    tabConfig.displayDirectives?.alignLeft ||
                                    config.displayDirectives.alignLeft,
                            })}
                        >
                            {rawValue} {indicator}
                        </span>
                    );
                };
                break;
            case "listingDate":
                if (locale === "de_DE") {
                    headerParentClassName = `${headerParentClassName} w-[135px] whitespace-normal`;
                }
                break;
            default:
                if (tabConfig.parent === "performance") {
                    colDef.cell = (info) => {
                        const value = info.getValue() as string;
                        const launchDate = info.row.original.data.launchDate;
                        const launchMsg =
                            launchDate && launchDate !== "-"
                                ? `${tx("Launch date").result}: ${launchDate}`
                                : tx("This share class has not launched yet")
                                      .result;
                        if (value === "-" || value === undefined) {
                            return (
                                <Tooltip content={launchMsg}>
                                    <div className="border-core-blue w-4 border-b border-dashed">
                                        -
                                    </div>
                                </Tooltip>
                            );
                        }
                        return <>{value}</>;
                    };
                }
                if (tabConfig.key === "prices") {
                    if (/date$/.test(prop)) {
                        cellParentClassName = `${cellParentClassName} w-[20%] ${
                            // /(nav|bid\/offer)/gi.test(fundDeps?.priceType ?? "")
                            //     ? "pl-[8em]"
                            //     : ""
                            ""
                        }`;
                        headerParentClassName = `${headerParentClassName} ${
                            // /(nav|bid\/offer)/gi.test(fundDeps?.priceType ?? "")
                            //     ? "pl-[8em]"
                            //     : ""
                            ""
                        }`;
                    }
                    if (/(price)$/i.test(prop)) {
                        cellParentClassName = `${cellParentClassName} w-[12%] ${
                            // prop === "midPrice" ? "pl-[5em]" : ""
                            ""
                        }`;
                        headerParentClassName = `${headerParentClassName} ${
                            // prop === "midPrice" ? "pl-[5em]" : ""
                            ""
                        }`;
                    }
                }
                break;
        }
        if (config.displayDirectives || tabConfig.displayDirectives) {
            const previousConfig = arr[i - 1];
            colDef.meta = handleDisplayDirectives(
                config.displayDirectives as ColumnDisplayDirectives,
                tabConfig.displayDirectives as TabDisplayDirectives,
                {
                    headerParentClassName,
                    cellParentClassName,
                },
                previousConfig?.displayDirectives,
            );
        } else {
            colDef.meta = { headerParentClassName, cellParentClassName };
        }

        return columnHelper.accessor(accessor, colDef);
    });
}
